import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { AuthTabs } from '../auth/AuthTabs';
import { supabase } from '../../lib/supabase';
import { LandingNav } from './LandingNav';
import { HeroSection } from './HeroSection';
import { CapabilityStrip } from './CapabilityStrip';
import { WhyBidWin } from './WhyBidWin';
import { EvidenceChipSection } from './EvidenceChipSection';
import { ScoringSection } from './ScoringSection';
import { WorkflowSection } from './WorkflowSection';
import { ProductShowcase } from './ProductShowcase';
import { CtaBand } from './CtaBand';
import { LandingFooter } from './LandingFooter';
import { PricingSection } from './PricingSection';
import { BlogSection } from './BlogSection';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  featured: boolean;
  read_time: string;
  content: string;
  keywords: string[];
  published: boolean;
}

export function LandingPage() {
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [isAnnual, setIsAnnual] = useState(true);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
  const [, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFeaturedPosts();
  }, []);

  const loadFeaturedPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .eq('featured', true)
        .order('date', { ascending: false })
        .limit(3);

      if (fetchError) {
        console.error('Error loading featured posts:', fetchError);
        setError('Unable to load featured posts. Please try again later.');
        return;
      }

      // Ensure we have valid data
      const validPosts = data?.filter(post =>
        post.title &&
        post.excerpt &&
        post.date
      ) || [];

      setFeaturedPosts(validPosts);
    } catch (error) {
      console.error('Error loading featured posts:', error);
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const nextFeatured = () => {
    setCurrentFeaturedIndex(current =>
      current === featuredPosts.length - 1 ? 0 : current + 1
    );
  };

  const prevFeatured = () => {
    setCurrentFeaturedIndex(current =>
      current === 0 ? featuredPosts.length - 1 : current - 1
    );
  };

  const openSignIn = () => setShowSignInModal(true);
  const openRequestAccess = () => setShowSignUpModal(true);

  return (
    <div className="min-h-screen bg-bw-app text-bw-ink font-sans flex flex-col">
      <LandingNav onSignIn={openSignIn} onRequestAccess={openRequestAccess} />

      <main className="flex-1">
        <HeroSection onRequestAccess={openRequestAccess} onSignIn={openSignIn} />
        <CapabilityStrip />
        <WhyBidWin />
        <EvidenceChipSection />
        <ScoringSection />
        <WorkflowSection />
        <ProductShowcase />

        {/* Pricing + Blog retained (deeper redesign ships with the article engine) */}
        <PricingSection
          isAnnual={isAnnual}
          onToggleAnnual={() => setIsAnnual(!isAnnual)}
          onGetStarted={openRequestAccess}
        />
        <BlogSection
          featuredPosts={featuredPosts}
          currentFeaturedIndex={currentFeaturedIndex}
          onPrevFeatured={prevFeatured}
          onNextFeatured={nextFeatured}
          onSetCurrentFeatured={setCurrentFeaturedIndex}
        />

        <CtaBand onRequestAccess={openRequestAccess} onSignIn={openSignIn} />
      </main>

      <LandingFooter />

      {/* Sign In Modal */}
      {showSignInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-bw-surface rounded-lg shadow-panel max-w-md w-full relative">
            <button
              onClick={() => setShowSignInModal(false)}
              className="absolute top-4 right-4 text-bw-muted hover:text-bw-ink z-10"
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
            <div className="p-8">
              <AuthTabs
                initialTab="signin"
                onSwitchToSignUp={() => {
                  setShowSignInModal(false);
                  setShowSignUpModal(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sign Up / Request Access Modal */}
      {showSignUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-bw-surface rounded-lg shadow-panel max-w-md w-full relative">
            <button
              onClick={() => setShowSignUpModal(false)}
              className="absolute top-4 right-4 text-bw-muted hover:text-bw-ink z-10"
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
            <div className="p-8">
              <AuthTabs
                initialTab="signup"
                onSwitchToSignIn={() => {
                  setShowSignUpModal(false);
                  setShowSignInModal(true);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
